/**
 * Silent activity logging (feature #9).
 *
 * Every ActivityEvent is enqueued and flushed to `activity_logs` in a debounced
 * batch (Firestore writeBatch). Each doc is { userId, event, meta?, ts } — the
 * exact ActivityLog shape from @mitang/shared. Security rules allow a signed-in
 * user to CREATE logs where userId == their uid, so this needs an authed user.
 *
 * Design:
 *  - Debounce window coalesces bursts (e.g. rapid scrolling → viewed_product).
 *  - Hard cap flush when the queue grows, so we never lose too much.
 *  - flushNow() is called on session_end / app pause so nothing is dropped.
 *  - Offline: writeBatch is queued by Firestore's persistence layer and sent
 *    when back online, so we simply await/ignore transient failures.
 *
 * COST CONTROL (this was ~10 writes per session, the biggest write load in the
 * system): events are DEDUPED per session — the same product/category/search is
 * recorded once, not once per revisit — and purely-cosmetic events are dropped.
 * The admin analytics (sessions, hot leads, churn, most-active) all still work
 * because they count distinct users/products, not repeats.
 */
import { collection, doc, writeBatch } from 'firebase/firestore';
import type { ActivityEvent, ActivityLog } from '@mitang/shared';
import { COLLECTIONS } from '@mitang/shared';
import { db } from '../firebase';
import { throttleGate } from '../localCache';

const DEBOUNCE_MS = 4000;
const MAX_QUEUE_BEFORE_FLUSH = 25;
const MAX_BATCH = 400; // under Firestore's 500-write batch limit

/** Events that carry no analytical value worth a write. */
const DROPPED_EVENTS: ReadonlySet<string> = new Set(['filter_used', 'session_end']);

/**
 * Events recorded at most ONCE per (event + target) per app session. The
 * "target" is the product/category/query in meta, so a dealer bouncing between
 * two products still yields two records — just not twenty.
 */
const DEDUPED_EVENTS: ReadonlySet<string> = new Set([
  'viewed_product',
  'viewed_category',
  'searched',
]);

/**
 * Events recorded at most once per HOUR per device (survives app restarts).
 * Dealers open the app dozens of times a day; "opened 40 times" and "opened
 * twice" both mean *active today*, so hourly resolution keeps every dashboard
 * meaningful at a fraction of the writes.
 */
const HOURLY_EVENTS: ReadonlySet<string> = new Set(['app_open']);
const HOUR_MS = 60 * 60 * 1000;

type QueuedLog = ActivityLog;

let queue: QueuedLog[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let currentUserId: string | null = null;
/** Dedupe keys already recorded this session (cleared on user change). */
let sessionKeys = new Set<string>();

/** Set/clear the user whose logs we write. Call on auth change. */
export function setActivityUser(userId: string | null): void {
  currentUserId = userId;
  sessionKeys = new Set();
}

/** Stable key for the dedupe set: event + its primary target. */
function dedupeKey(event: string, meta?: Record<string, string | number>): string {
  const target = meta?.productId ?? meta?.category ?? meta?.query ?? '';
  return `${event}:${String(target)}`;
}

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flushNow();
  }, DEBOUNCE_MS);
}

/** Enqueue an activity event. No-op if there is no signed-in user. */
export function logActivity(
  event: ActivityEvent,
  meta?: Record<string, string | number>,
): void {
  if (!currentUserId) return;
  if (DROPPED_EVENTS.has(event)) return;
  if (HOURLY_EVENTS.has(event) && !throttleGate(`${event}.${currentUserId}`, HOUR_MS)) return;
  if (DEDUPED_EVENTS.has(event)) {
    const key = dedupeKey(event, meta);
    if (sessionKeys.has(key)) return;
    sessionKeys.add(key);
  }
  const entry: QueuedLog = {
    userId: currentUserId,
    event,
    ts: Date.now(),
    ...(meta ? { meta } : {}),
  };
  queue.push(entry);
  if (queue.length >= MAX_QUEUE_BEFORE_FLUSH) {
    void flushNow();
  } else {
    scheduleFlush();
  }
}

/** Flush the queue immediately. Safe to call anytime (e.g. on pause/unload). */
export async function flushNow(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;

  const pending = queue;
  queue = [];

  try {
    const logsCol = collection(db, COLLECTIONS.activityLogs);
    // Chunk into batches under the write limit.
    for (let i = 0; i < pending.length; i += MAX_BATCH) {
      const slice = pending.slice(i, i + MAX_BATCH);
      const batch = writeBatch(db);
      for (const entry of slice) {
        batch.set(doc(logsCol), entry);
      }
      await batch.commit();
    }
  } catch {
    // Re-queue on hard failure so we retry on the next flush.
    queue = pending.concat(queue);
  }
}
